/**
 * Générateur — Définir les nombres relatifs.
 * Attendu ID : 910 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 910.json) : « Définir les nombres relatifs. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-21) :
 *
 *   Carte « mots » — Le mot qui convient (niveau 1)
 *     texte_trou_drag : étiquettes = liste fermée (positif, négatif, plus,
 *     relatifs, opposé, moins) ; une sous-question par tirage (a-e). La
 *     sous-question d (« 0 est à la fois positif et négatif ») a un seul trou,
 *     le mot troué tourne. Nombres tirés dans -20..20, 0 ou 1 décimale.
 *
 *   Carte « ecritures » — Écritures d'un même nombre (niveau 1)
 *     qcm_multiple à compte annoncé variable (2 ou 3). La cible est écrite
 *     sans signe, avec +, avec - ou entre parenthèses ; le vivier d'écritures
 *     est symétrique (chaque écriture est juste pour un signe de cible et
 *     fausse pour l'autre), aucune option n'a un statut fixe.
 *
 *   Carte « definitions » — Vrai ou faux sur les relatifs (niveaux 1 et 4)
 *     tableau_vf : 4 lignes tirées parmi 7 gabarits à prédicat variable (chaque
 *     gabarit a une forme vraie et une forme fausse), mélange V/F garanti.
 *
 *   Carte « oppose » — L'opposé et la somme nulle (niveaux 2 et 4)
 *     texte (saisie) : « quel nombre ajouter à a pour obtenir 0 ? » ;
 *     qcm_unique : l'opposé de a parmi -a, a, 0 ;
 *     tableau_vf : égalités a + (-a) = 0 / a - (-a) = 0, « l'opposé de a est b »,
 *     « x est son propre opposé » (0 traité explicitement, jamais de zéro signé).
 *
 *   Carte « soustraction » — La soustraction rendue possible (niveaux 3 et 4)
 *     texte (saisie) : a - b avec b > a, résultat négatif ;
 *     texte_trou_saisie : le nombre négatif donné répond à a - {{b}}.
 *
 *   Carte « thermo » — Chaud et froid (thermomètres) (niveau 5)
 *     texte (saisie) + qcm_unique. Thermomètre SVG généré, 2 régimes de
 *     finesse (graduation à l'unité / au dixième). Plage -10..10 °C.
 *
 *   Carte « contexte » — Situations et nombres relatifs (niveau 5)
 *     qcm_multiple : les deux situations (gain/perte, température, altitude,
 *     solde) qui correspondent à un nombre relatif donné.
 *
 * Spécification (910.json) :
 *   Bornes appliquées : amplitude_du_nombre -20..20 ; nombre_de_decimales 0..1 ;
 *     signe devant un positif : aucun ou + (les trois écritures sont rencontrées) ;
 *     registres : somme nulle avec l'opposé, soustraction rendue possible,
 *     contexte de gain et de perte (température, altitude, solde) ;
 *     premier terme de la soustraction impossible 0..20, second terme strictement
 *     supérieur, 20 au plus ; séries de 3 à 8 propositions (V/F : 4 lignes,
 *     QCM multiples : 4 ou 5 options).
 *   Exclusions : aucune fraction ; aucune multiplication ni division ; aucun
 *     nombre à plus d'une décimale ; aucune proposition à statut fixe (gabarits
 *     V/F à prédicat variable, viviers de QCM symétriques) ; pas deux questions
 *     de même valeur dans une série (valeurs distinctes par construction) ;
 *     l'opposé de 0 s'écrit 0 (dix(0) = « 0 », jamais de zéro signé).
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code, lignes[].code
 *   ou erreurVisee) :
 *     « Croire qu'un nombre écrit sans signe n'est pas un nombre relatif »
 *         → sans_signe_pas_relatif (ecritures, definitions)
 *     « Opposer positif et relatif comme deux catégories exclusives »
 *         → positif_exclut_relatif (definitions)
 *     « Confondre l'opposé d'un nombre avec le même nombre réécrit avec un
 *       signe moins » → oppose_par_signe_moins (ecritures, oppose ×3)
 *     « Confondre ajouter l'opposé et soustraire, n moins son opposé vaut 0 »
 *         → moins_oppose_vaut_zero (oppose ×3)
 *     « Rattacher la notion de relatif aux seuls entiers »
 *         → relatif_reserve_aux_entiers (definitions)
 *     « Croire que le signe moins d'un négatif est un signe d'opération »
 *         → signe_moins_operation (ecritures, definitions, soustraction)
 *     Codes hors table (erreurs de lecture propres au niveau 5, signalés dans
 *     le rapport) : signe_inverse, graduation_voisine (thermo), signe_ignore
 *     (contexte), meme_nombre (oppose_qcm quand a est positif).
 *   Graduation → cartes :
 *     niveau 1 → mots, ecritures, definitions ; niveau 2 → oppose (entiers) ;
 *     niveau 3 → soustraction (entiers) ; niveau 4 → oppose et soustraction
 *     (tirages décimaux, un tirage sur deux) et definitions (lignes à décimal) ;
 *     niveau 5 → thermo, contexte.
 *   Non tranché : rétroaction écrite par proposition (type_reponse) non rendue
 *     par l'application, non implémentée ; registre « repérage sur une droite
 *     graduée » listé sans niveau de graduation, non ajouté ; codes hors table
 *     ci-dessus.
 *
 * Politique du cluster : signe « - » = tiret clavier PARTOUT (jamais U+2212) ;
 * aucune règle de calcul sur les relatifs ; « valeur absolue » en mots.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 910;

const L_MOTS = 'Le mot qui convient';
const L_THERMO = 'Chaud et froid (thermomètres)';
const L_ECRITURES = 'Écritures d\'un même nombre';
const L_DEFS = 'Vrai ou faux sur les relatifs';
const L_OPPOSE = 'L\'opposé et la somme nulle';
const L_SOUSTRACTION = 'La soustraction rendue possible';
const L_CONTEXTE = 'Situations et nombres relatifs';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Valeurs manipulées en DIXIÈMES entiers (zéro flottant). dix(-83) = "-8,3".
// dix(0) = "0" : l'opposé de 0 s'écrit 0, jamais avec un signe.
const dix = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};
// Même valeur pour KaTeX (virgule protégée).
const texNum = (d) => dix(d).replace(',', '{,}');
// Un nombre négatif entre parenthèses dans une somme ou une différence.
const par = (d) => (d < 0 ? `(${texNum(d)})` : texNum(d));

/**
 * Valeur absolue tirée dans les bornes de la spec : entier 1..20, ou décimal
 * non entier à une décimale 0,1..19,9 (en dixièmes).
 */
const tirerAbs = (decimal) => {
    if (!decimal) return randInt(1, 20) * 10;
    let v = randInt(1, 199);
    if (v % 10 === 0) v += 1;
    return v;
};

const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

// ═════════ Carte « mots » — Le mot qui convient ═════════

const LISTE_MOTS = ['positif', 'négatif', 'plus', 'relatifs', 'opposé', 'moins'];

/** Un nombre relatif « d'affichage » aléatoire, dans -20..20 (dixièmes). */
const nombreAffiche = () => {
    const v = tirerAbs(randomChoice([true, false]));
    return randomChoice([true, false]) ? dix(v) : dix(-v);
};

const genMots = () => {
    const variante = randomChoice(['a', 'b', 'c', 'd', 'e']);
    let phrase;
    if (variante === 'a') {
        const uns = new Set();
        for (let essai = 0; essai < 40 && uns.size < 4; essai++) uns.add(nombreAffiche());
        ['-3', '5', '-9,3', '12,7'].forEach(x => { if (uns.size < 4) uns.add(x); }); // repli dans les bornes
        phrase = `${[...uns].join(' ; ')} et 0 sont des nombres {{relatifs}}.`;
    } else if (variante === 'b') {
        const n = randInt(2, 20);
        phrase = `Le nombre +${n} est un nombre {{positif}}. Il peut aussi s'écrire sans le signe {{plus}}.`;
    } else if (variante === 'c') {
        const n = randInt(2, 20);
        phrase = `Le nombre (-${n}) est un nombre {{négatif}}. On ne peut pas supprimer le signe {{moins}}.`;
    } else if (variante === 'd') {
        phrase = randomChoice([
            'Le nombre 0 est à la fois {{positif}} et négatif.',
            'Le nombre 0 est à la fois positif et {{négatif}}.',
        ]);
    } else {
        const d = dix(tirerAbs(randomChoice([true, false])));
        phrase = `(-${d}) est l'{{opposé}} de ${d}.`;
    }
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète avec le mot qui convient.</b></p>',
        { ...opts(L_MOTS), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, LISTE_MOTS) });
    payload._meta.params = { variante, niveau: 1 };
    return payload;
};

// ═════════ Carte « thermo » — Thermomètres ═════════

/**
 * Tire un thermomètre : régime 'entier' (graduation 1 °C, plage -10..10) ou
 * 'dixieme' (graduation 0,1 °C, plage de 2 °C). La température tombe TOUJOURS
 * sur une graduation, jamais sur une borne.
 */
const tirerThermo = () => {
    if (randomChoice([true, false])) {
        // graduations à l'unité, étiquettes tous les 5 °C
        let t = 0;
        while (t === 0) t = randInt(-9, 9) * 10;
        return { min: -100, max: 100, pas: 10, etiq: 50, t };
    }
    // graduations au dixième, étiquettes aux entiers — température non entière
    const base = randInt(-5, 3) * 10;
    let k = randInt(1, 19);
    if (k % 10 === 0) k += 1;
    return { min: base, max: base + 20, pas: 1, etiq: 10, t: base + k };
};

/** SVG thermomètre — parsable : étiquettes <text class='tl'>, mercure <rect class='hg'>. */
const svgThermo = (cfg) => {
    const W = 150, H = 300, yBas = 240, yHaut = 30, xTube = 78;
    const Y = (v) => Math.round((yBas - ((v - cfg.min) / (cfg.max - cfg.min)) * (yBas - yHaut)) * 10) / 10;
    let grads = '';
    for (let v = cfg.min; v <= cfg.max; v += cfg.pas) {
        const y = Y(v);
        const majeure = v % cfg.etiq === 0;
        grads += `<line x1='${xTube - (majeure ? 14 : 8)}' y1='${y}' x2='${xTube - 2}' y2='${y}' stroke='currentColor' stroke-width='${majeure ? 1.4 : 0.7}'/>`;
        if (majeure) {
            grads += `<text class='tl' x='${xTube - 18}' y='${y}' dy='4' font-size='12' text-anchor='end' fill='currentColor'>${dix(v)}</text>`;
        }
    }
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}' viewBox='0 0 ${W} ${H}' font-family='sans-serif'>`
        + `<rect x='${xTube}' y='${yHaut - 8}' width='16' height='${yBas - yHaut + 16}' rx='8' fill='none' stroke='currentColor' stroke-width='1.6'/>`
        + `<circle cx='${xTube + 8}' cy='${yBas + 26}' r='15' fill='#dc2626'/>`
        + `<rect class='hg' x='${xTube + 4}' y='${Y(cfg.t)}' width='8' height='${Math.round((yBas + 20 - Y(cfg.t)) * 10) / 10}' fill='#dc2626'/>`
        + grads
        + `<text x='${xTube + 26}' y='${yHaut + 6}' font-size='12' fill='currentColor'>°C</text>`
        + `</svg>`;
};

const CONSIGNE_SIGNE = 'Écris le nombre avec son signe s\'il est négatif (ex. : -4 ou -1,5). Un nombre positif peut s\'écrire avec ou sans le signe +.';

const genThermoSaisie = () => {
    const cfg = tirerThermo();
    const payload = makeSaisie(dix(cfg.t),
        `<p><b>Quelle température, en °C, ce thermomètre indique-t-il ?</b> ${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_THERMO), suffixe: '°C', elements_additionnels: svgToElement(svgThermo(cfg)) });
    payload._meta.params = { t: cfg.t, pas: cfg.pas, niveau: 5 };
    return payload;
};

const genThermoQcm = () => {
    const cfg = tirerThermo();
    const correct = dix(cfg.t);
    // Erreurs de lecture propres au registre (hors table des erreurs visées) :
    // signe inversé, graduation voisine.
    const candidats = [
        { valeur: dix(-cfg.t), code: 'signe_inverse' },
        { valeur: dix(cfg.t + cfg.pas), code: 'graduation_voisine' },
        { valeur: dix(cfg.t - cfg.pas), code: 'graduation_voisine' },
        { valeur: dix(cfg.t + 2 * cfg.pas), code: 'graduation_voisine' },
    ];
    const distracteurs = [];
    candidats.forEach(c => {
        if (distracteurs.length < 3 && c.valeur !== correct && !distracteurs.some(d => d.valeur === c.valeur)) distracteurs.push(c);
    });
    const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
        '<p><b>Quelle température, en °C, ce thermomètre indique-t-il ?</b></p>',
        { ...opts(L_THERMO), elements_additionnels: svgToElement(svgThermo(cfg)) });
    payload._meta.params = { t: cfg.t, pas: cfg.pas, niveau: 5, distracteurs };
    return payload;
};

// ═════════ Carte « ecritures » — Écritures d'un même nombre ═════════

const MOTS_COMPTE = { 2: 'deux', 3: 'trois' };

/**
 * Vivier symétrique des écritures d'un nombre de valeur absolue s :
 * `positif` = l'écriture désigne +s (sinon -s). Le code est l'erreur d'un
 * élève qui se trompe sur cette écriture (la coche à tort, ou l'oublie).
 */
const vivierEcritures = (s) => [
    { t: s, positif: true, codeSiVrai: 'sans_signe_pas_relatif', codeSiFaux: 'signe_moins_operation' },
    { t: `+${s}`, positif: true, codeSiVrai: 'sans_signe_pas_relatif', codeSiFaux: 'signe_moins_operation' },
    { t: `(+${s})`, positif: true, codeSiVrai: 'sans_signe_pas_relatif', codeSiFaux: 'signe_moins_operation' },
    { t: `l'opposé de -${s}`, positif: true, codeSiVrai: 'oppose_par_signe_moins', codeSiFaux: 'oppose_par_signe_moins' },
    { t: `l'opposé de (-${s})`, positif: true, codeSiVrai: 'oppose_par_signe_moins', codeSiFaux: 'oppose_par_signe_moins' },
    { t: `-${s}`, positif: false, codeSiVrai: 'signe_moins_operation', codeSiFaux: 'oppose_par_signe_moins' },
    { t: `(-${s})`, positif: false, codeSiVrai: 'signe_moins_operation', codeSiFaux: 'oppose_par_signe_moins' },
    { t: `l'opposé de ${s}`, positif: false, codeSiVrai: 'oppose_par_signe_moins', codeSiFaux: 'oppose_par_signe_moins' },
    { t: `l'opposé de +${s}`, positif: false, codeSiVrai: 'oppose_par_signe_moins', codeSiFaux: 'oppose_par_signe_moins' },
];

const genEcritures = () => {
    const s = dix(tirerAbs(randomChoice([true, false])));
    const positif = randomChoice([true, false]);
    // La cible rencontre les écritures : sans signe, avec +, avec -, entre parenthèses.
    const cible = positif ? randomChoice([s, `+${s}`]) : randomChoice([`-${s}`, `(-${s})`]);
    const vivier = vivierEcritures(s).filter(e => e.t !== cible);
    const nbCorrects = randomChoice([2, 3]);
    const corrects = shuffle(vivier.filter(e => e.positif === positif)).slice(0, nbCorrects);
    const faux = shuffle(vivier.filter(e => e.positif !== positif)).slice(0, 5 - nbCorrects);
    const payload = makeQcm([...corrects, ...faux].map(e => e.t), corrects.map(e => e.t),
        `<p><b>Coche les ${MOTS_COMPTE[nbCorrects]} écritures qui désignent le même nombre que ${cible}.</b></p>`,
        opts(L_ECRITURES), true);
    payload._meta.params = {
        cible, niveau: 1,
        corrects: corrects.map(e => ({ valeur: e.t, code: e.codeSiVrai })),
        distracteurs: faux.map(e => ({ valeur: e.t, code: e.codeSiFaux })),
    };
    return payload;
};

// ═════════ Carte « definitions » — Vrai ou faux sur les relatifs ═════════

/**
 * Gabarits à prédicat variable : chaque gabarit produit une ligne vraie ou
 * une ligne fausse selon `vrai`, sur les valeurs n (entier 1..20) et d
 * (décimal non entier). Le code nomme l'erreur d'un élève qui se trompe.
 */
const GABARITS_DEFS = [
    { id: 'sans_signe', code: 'sans_signe_pas_relatif',
        ligne: (v, n) => `${n} ${v ? 'est' : 'n\'est pas'} un nombre relatif.` },
    { id: 'zero', code: 'sans_signe_pas_relatif',
        ligne: (v) => `0 ${v ? 'est' : 'n\'est pas'} un nombre relatif.` },
    { id: 'positif_relatif', code: 'positif_exclut_relatif',
        ligne: (v, n) => `+${n} ${v ? 'est' : 'n\'est pas'} un nombre relatif.` },
    { id: 'categorie_positif', code: 'positif_exclut_relatif',
        ligne: (v) => `Un nombre positif ${v ? 'est aussi' : 'n\'est jamais'} un nombre relatif.` },
    { id: 'decimal_relatif', code: 'relatif_reserve_aux_entiers',
        ligne: (v, n, d) => (v ? `-${d} est un nombre relatif.` : `-${d} n'est pas un nombre relatif, car ce n'est pas un entier.`) },
    { id: 'entiers_seulement', code: 'relatif_reserve_aux_entiers',
        ligne: (v, n, d) => (v ? `Un nombre décimal comme ${d} est un nombre relatif.` : 'Les nombres relatifs sont tous des nombres entiers.') },
    { id: 'signe_moins', code: 'signe_moins_operation',
        ligne: (v, n) => `Dans -${n}, le signe moins ${v ? 'fait partie du nombre' : 'indique une soustraction'}.` },
    { id: 'meme_nombre', code: 'sans_signe_pas_relatif',
        ligne: (v, n) => (v ? `${n} et +${n} désignent le même nombre.` : `${n} et -${n} désignent le même nombre.`) },
];

/** Tire nb vérités avec au moins une vraie et une fausse (mélange garanti). */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

const genDefinitionsVf = () => {
    const n = randInt(1, 20);
    const d = dix(tirerAbs(true));
    const gabarits = shuffle(GABARITS_DEFS).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({ template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], n, d) }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_DEFS));
    payload._meta.params = { n, d, niveau: gabarits.some(g => g.id === 'decimal_relatif' || g.id === 'entiers_seulement') ? 4 : 1, lignes };
    return payload;
};

// ═════════ Carte « oppose » — L'opposé et la somme nulle ═════════

/** Un nombre relatif non nul (dixièmes), et son affichage : un positif s'écrit avec ou sans +. */
const tirerNombre = (decimal) => {
    const a = randomChoice([1, -1]) * tirerAbs(decimal);
    const aff = (a > 0 && randomChoice([true, false]) ? '+' : '') + dix(a);
    return { a, aff };
};

const genOpposeSaisie = () => {
    const decimal = randomChoice([false, true]);
    const zero = randInt(1, 10) === 1; // le zéro, une fois sur dix : son opposé s'écrit 0
    const { a, aff } = zero ? { a: 0, aff: '0' } : tirerNombre(decimal);
    const forme = randomChoice(['somme', 'oppose']);
    const question = forme === 'somme'
        ? `Quel nombre faut-il ajouter à ${aff} pour obtenir 0 ?`
        : `Un nombre et son opposé ont une somme nulle. Quel est l'opposé de ${aff} ?`;
    const payload = makeSaisie(dix(-a),
        `<p><b>${question}</b> ${CONSIGNE_SIGNE}</p>`,
        opts(L_OPPOSE));
    payload._meta.params = {
        a, forme, niveau: decimal && !zero ? 4 : 2,
        // Ce que provoque la saisie : réécrire -7 en -7 (a négatif) ; répondre
        // le nombre lui-même parce que 7 - 7 = 0 (a positif).
        ...(a === 0 ? {} : { erreurVisee: a < 0 ? 'oppose_par_signe_moins' : 'moins_oppose_vaut_zero' }),
    };
    return payload;
};

const genOpposeQcm = () => {
    const decimal = randomChoice([false, true]);
    const { a, aff } = tirerNombre(decimal);
    const correct = dix(-a);
    const distracteurs = [
        // le nombre lui-même : « l'opposé de -7, c'est -7 » (a < 0) ; hors table quand a > 0
        { valeur: dix(a), code: a < 0 ? 'oppose_par_signe_moins' : 'meme_nombre' },
        // 0 : le résultat de a - a, confondu avec l'opposé
        { valeur: '0', code: 'moins_oppose_vaut_zero' },
    ];
    const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
        `<p><b>Un nombre et son opposé ont une somme nulle. Quel est l'opposé de ${aff} ?</b></p>`,
        opts(L_OPPOSE));
    payload._meta.params = { a, niveau: decimal ? 4 : 2, distracteurs };
    return payload;
};

/** Quatre valeurs absolues distinctes (dixièmes), entiers et décimaux mêlés. */
const tirerAbsDistincts = (nb) => {
    const vus = new Set();
    for (let essai = 0; essai < 80 && vus.size < nb; essai++) vus.add(tirerAbs(randomChoice([true, false])));
    [10, 70, 35, 128].forEach(x => { if (vus.size < nb) vus.add(x); }); // repli dans les bornes
    return [...vus].slice(0, nb);
};

const genOpposeVf = () => {
    const [v1, v2, v3, v4] = tirerAbsDistincts(4);
    const signe = () => randomChoice([1, -1]);
    const a1 = signe() * v1, a2 = signe() * v2, a3 = signe() * v3;
    const verites = tirerVerites(4);
    const lignes = [
        // a + b = 0 : vrai si b est l'opposé, faux si b est a lui-même
        { template: 'somme_nulle', code: 'oppose_par_signe_moins', vrai: verites[0],
            texte: tex(`${texNum(a1)} + ${par(verites[0] ? -a1 : a1)} = 0`) },
        // a - b = 0 : vrai si b = a, faux si b est l'opposé (soustraire n'est pas ajouter l'opposé)
        { template: 'difference_nulle', code: 'moins_oppose_vaut_zero', vrai: verites[1],
            texte: tex(`${texNum(a2)} - ${par(verites[1] ? a2 : -a2)} = 0`) },
        { template: 'oppose_de', code: 'oppose_par_signe_moins', vrai: verites[2],
            texte: `L'opposé de ${dix(a3)} est ${dix(verites[2] ? -a3 : a3)}.` },
        // 0 est son propre opposé ; un nombre non nul, jamais
        { template: 'propre_oppose', code: 'oppose_par_signe_moins', vrai: verites[3],
            texte: `Le nombre ${verites[3] ? '0' : dix(signe() * v4)} est son propre opposé.` },
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(
        melange.map(l => l.texte),
        melange.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_OPPOSE));
    payload._meta.params = { a1, a2, a3, niveau: [v1, v2, v3].some(v => v % 10 !== 0) ? 4 : 2, lignes: melange };
    return payload;
};

// ═════════ Carte « soustraction » — La soustraction rendue possible ═════════

/** a et b (dixièmes) : 0 ≤ a < b ≤ 20, entiers tous deux ou décimaux à une décimale. */
const tirerSoustraction = () => {
    const decimal = randomChoice([false, true]);
    if (!decimal) {
        const a = randInt(0, 19) * 10;
        const b = randInt(a / 10 + 1, 20) * 10;
        return { a, b, decimal };
    }
    let a = randInt(0, 198);
    const b = randInt(a + 1, 200);
    // au moins l'un des deux non entier, sinon on serait dans le cas entier
    // (deux multiples de 10 : a ≤ 190 et b ≥ a + 10, donc a + 1 < b reste vrai)
    if (a % 10 === 0 && b % 10 === 0) a += 1;
    return { a, b, decimal };
};

const genSoustractionSaisie = () => {
    const { a, b, decimal } = tirerSoustraction();
    const payload = makeSaisie(dix(a - b),
        `<p><b>Avec les nombres positifs seulement, la soustraction ${tex(`${texNum(a)} - ${texNum(b)}`)} n'a pas de résultat. `
        + 'Avec les nombres relatifs, elle en a un.</b> '
        + 'Écris ce résultat avec son signe (ex. : -4 ou -1,5).</p>',
        opts(L_SOUSTRACTION));
    // Écrire b - a sans signe : le signe du résultat pris pour celui de l'opération
    payload._meta.params = { a, b, niveau: decimal ? 4 : 3, erreurVisee: 'signe_moins_operation' };
    return payload;
};

const genSoustractionTrou = () => {
    const { a, b, decimal } = tirerSoustraction();
    const phrase = `Le nombre ${dix(a - b)} est le résultat de la soustraction ${dix(a)} - {{${dix(b)}}}, impossible dans les nombres positifs.`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète la soustraction dont ce nombre négatif est le résultat.</b></p>',
        { ...opts(L_SOUSTRACTION), mode: 'saisie' });
    payload._meta.params = { a, b, niveau: decimal ? 4 : 3 };
    return payload;
};

// ═════════ Carte « contexte » — Situations et nombres relatifs ═════════

const SITUATIONS = [
    { id: 'argent', pos: (v) => `un gain de ${v} €`, neg: (v) => `une perte de ${v} €` },
    { id: 'temperature', pos: (v) => `une température de ${v} °C au-dessus de 0`, neg: (v) => `une température de ${v} °C au-dessous de 0` },
    { id: 'altitude', pos: (v) => `une altitude de ${v} m au-dessus du niveau de la mer`, neg: (v) => `une profondeur de ${v} m sous le niveau de la mer` },
    { id: 'solde', pos: (v) => `un solde de ${v} € en plus sur un compte`, neg: (v) => `un solde de ${v} € en moins sur un compte` },
];

const genContexteQcm = () => {
    const v = dix(tirerAbs(randomChoice([true, false])));
    const positif = randomChoice([true, false]);
    const cible = positif ? randomChoice([v, `+${v}`]) : `-${v}`;
    const [s1, s2, s3] = shuffle(SITUATIONS).slice(0, 3);
    const corrects = [s1, s2].map(s => (positif ? s.pos(v) : s.neg(v)));
    // Le signe ignoré : la même situation dans l'autre sens (hors table des erreurs visées)
    const faux = [s1, s3].map(s => (positif ? s.neg(v) : s.pos(v)));
    const payload = makeQcm(shuffle([...corrects, ...faux]), corrects,
        `<p><b>Coche les deux situations que le nombre ${cible} représente.</b></p>`,
        opts(L_CONTEXTE), true);
    payload._meta.params = { cible, niveau: 5, distracteurs: faux.map(f => ({ valeur: f, code: 'signe_ignore' })) };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    mots_texte_trou_drag: genMots,
    thermo_texte: genThermoSaisie,
    thermo_qcm_unique: genThermoQcm,
    ecritures_qcm_multiple: genEcritures,
    definitions_vf: genDefinitionsVf,
    oppose_texte: genOpposeSaisie,
    oppose_qcm_unique: genOpposeQcm,
    oppose_vf: genOpposeVf,
    soustraction_texte: genSoustractionSaisie,
    soustraction_texte_trou_saisie: genSoustractionTrou,
    contexte_qcm_multiple: genContexteQcm,
};

const FORMAT_LABELS = {
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Trous', texte: 'Saisie', qcm_unique: 'QCM',
    qcm_multiple: 'QCM Multi', vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'mots', label: L_MOTS, formatsList: buildFormats('mots') },
    { id: 'ecritures', label: L_ECRITURES, formatsList: buildFormats('ecritures') },
    { id: 'definitions', label: L_DEFS, formatsList: buildFormats('definitions') },
    { id: 'oppose', label: L_OPPOSE, formatsList: buildFormats('oppose') },
    { id: 'soustraction', label: L_SOUSTRACTION, formatsList: buildFormats('soustraction') },
    { id: 'thermo', label: L_THERMO, formatsList: buildFormats('thermo') },
    { id: 'contexte', label: L_CONTEXTE, formatsList: buildFormats('contexte') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
